"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MessageCircleQuestionMark } from "lucide-react";
const faqs = [{
  question: "What bookkeeping services do you provide?",
  answer: "We handle daily transaction recording, bank reconciliations, accounts payable/receivable, and monthly financial reporting. Our goal is to ensure your ledger is always accurate and audit-ready."
}, {
  question: "How do you handle tax preparation and compliance?",
  answer: "LEDGER manages all aspects of tax compliance, from sales tax filings to year-end preparation. We work proactively to identify deductions and ensure you meet all regulatory deadlines without stress."
}, {
  question: "Can you integrate with my existing cloud software?",
  answer: "Absolutely. We specialize in cloud-based tools like QuickBooks, Xero, and FreshBooks. We can sync your existing data and optimize your digital workflow for maximum transparency and speed."
}, {
  question: "Is my financial data secure with LEDGER?",
  answer: "Security is our top priority. We use bank-level encryption and multi-factor authentication for all data transfers and storage, ensuring your sensitive financial information remains confidential and protected."
}, {
  question: "Do you offer payroll processing services?",
  answer: "Yes, we provide comprehensive payroll management, including direct deposits, tax withholdings, and filing of quarterly payroll tax reports, allowing you to focus on growing your team."
}];
export default function CommonQuestions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: listRef,
    isVisible: isListVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="221646" id="common-questions" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`mb-12 text-center transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="mb-4 flex items-center justify-center gap-2 text-primary">
            <MessageCircleQuestionMark className="size-6" />
            <span className="font-default text-sm font-semibold uppercase tracking-wider">
              Expert Support
            </span>
          </div>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Common Questions
          </h2>
          <p className="mt-4 font-sans text-lg text-muted-foreground">
            Find clear answers to the most frequent inquiries about our 
            professional bookkeeping and accounting solutions.
          </p>
        </div>

        <div ref={listRef} className={`transition-all duration-700 delay-200 ease-out ${isListVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Accordion type="single" collapsible className="w-full rounded-lg border border-border bg-card shadow-lg backdrop-blur-sm">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border px-6 transition-colors data-[state=open]:bg-accent/10">
                <AccordionTrigger className="font-default text-base font-semibold text-foreground hover:no-underline hover:text-primary data-[state=open]:text-primary">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-mono text-sm text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div className={`mt-12 flex flex-col items-center justify-center gap-4 text-center transition-all duration-700 delay-400 ease-out sm:flex-row ${isListVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <p className="font-sans text-muted-foreground">
            Still have questions about your finances?
          </p>
          <Button asChild variant="default" className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity rounded-md">
            <Link to="#">
              Contact us for a consultation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}